import { enqueueSnackbar } from 'notistack';
import { clearAllAuthData } from './auth';
import type { ApiErrorResponse } from '@shared/interface';

/**
 * Centralized API error handler
 * Handles 401 errors with automatic logout and shows notifications for all errors
 */

interface ErrorHandlerOptions {
  /** Custom error message to show instead of API message */
  customMessage?: string;
  /** Whether to show notification (default: true) */
  showNotification?: boolean;
  /** Whether to handle 401 logout automatically (default: true) */
  handle401?: boolean;
}

/**
 * Handles API errors with automatic logout for 401 and notifications
 * @param response - Fetch Response object
 * @param defaultMessage - Default message if API doesn't provide one
 * @param options - Additional options for error handling
 * @returns Parsed response data if successful
 * @throws Error with proper message if request failed
 */
export const handleApiResponse = async <T>(
  response: Response,
  defaultMessage: string = 'An error occurred',
  options: ErrorHandlerOptions = {},
): Promise<T> => {
  const {
    customMessage,
    showNotification = true,
    handle401 = true,
  } = options;

  // Try to parse response body
  let data: any;
  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {};
  }

  // Handle successful response
  if (response.ok) {
    return data as T;
  }

  // Extract error message from response
  const apiError = data as ApiErrorResponse;
  let errorMessage: string;

  if (customMessage) {
    errorMessage = customMessage;
  } else if (Array.isArray(apiError.message)) {
    // Handle array of validation errors
    errorMessage = apiError.message.join('\n');
  } else if (apiError.message) {
    errorMessage = apiError.message;
  } else if (apiError.error) {
    errorMessage = apiError.error;
  } else if (response.statusText) {
    errorMessage = response.statusText;
  } else {
    errorMessage = defaultMessage;
  }

  // Handle 401 Unauthorized - Auto logout
  if (response.status === 401 && handle401) {
    if (showNotification) {
      enqueueSnackbar(errorMessage || 'Session expired. Please login again.', {
        variant: 'error',
        autoHideDuration: 3000,
        preventDuplicate: true,
      });
    }
    
    // Clear auth data and redirect to login
    clearAllAuthData();
    window.location.href = '/login';
    
    // Throw error to stop further execution
    const error = new Error(errorMessage);
    (error as any).response = { data: apiError, status: response.status };
    throw error;
  }

  // Show notification for other errors
  if (showNotification) {
    enqueueSnackbar(errorMessage, {
      variant: 'error',
      autoHideDuration: 5000,
      preventDuplicate: true,
    });
  }

  // Throw error with response data
  const error = new Error(errorMessage);
  (error as any).response = { data: apiError, status: response.status };
  throw error;
};

/**
 * Wrapper for fetch with automatic error handling
 * @param url - Request URL
 * @param init - Fetch init options
 * @param defaultMessage - Default error message
 * @param options - Error handler options
 * @returns Parsed response data
 */
export const fetchWithErrorHandling = async <T>(
  url: string,
  init?: RequestInit,
  defaultMessage?: string,
  options?: ErrorHandlerOptions,
): Promise<T> => {
  const response = await fetch(url, init);
  return handleApiResponse<T>(response, defaultMessage, options);
};

/**
 * Shows success notification
 * @param message - Success message to display
 * @param response - Optional API response object that might contain a message
 */
export const showSuccessNotification = (
  message: string,
  response?: any,
): void => {
  const displayMessage = response?.message || message;
  enqueueSnackbar(displayMessage, {
    variant: 'success',
    autoHideDuration: 3000,
    preventDuplicate: true,
  });
};

/**
 * Shows error notification
 * @param error - Error object or message
 * @param defaultMessage - Default message if error doesn't contain one
 */
export const showErrorNotification = (
  error: any,
  defaultMessage: string = 'An error occurred',
): void => {
  let message = defaultMessage;

  if (error?.response?.data) {
    const data = error.response.data as ApiErrorResponse;
    if (Array.isArray(data.message)) {
      message = data.message.join('\n');
    } else if (data.message) {
      message = data.message;
    } else if (data.error) {
      message = data.error;
    }
  } else if (error?.message) {
    message = error.message;
  } else if (typeof error === 'string') {
    message = error;
  }

  enqueueSnackbar(message, {
    variant: 'error',
    autoHideDuration: 5000,
    preventDuplicate: true,
  });
};
